import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { getPortfolioItems, getHeroBlurDataUrl } from "../utils/portfolioData";
import { getCollections, prefetchCollectionItems } from "../utils/collectionsData";
import { getLocations } from "../utils/locationsData";
import { thumbnailUrl } from "../utils/galleryImageUrls";
import { useScrollReveal } from "../hooks/useScrollReveal";
import "./HomePage.css";

// The home page is a *story*, deliberately distinct from /portfolio (the full ranked
// grid): a commanding hero, a small hand-varied set of features, a sense of place
// ("Around the world"), and the bodies of work. It never renders the portfolio
// masonry grid — that lives only on /portfolio.
const FEATURED_COUNT = 3;
// Curated collection spotlight (by key), shown in this order.
const PREFERRED_COLLECTIONS = ["big-cats", "landscape", "elephants"];
// Backdrop for the "Around the world" band (falls back to a wide frame if absent).
const WORLD_IMAGE_ID = "img-portfolio-night-sky-3";

const titleFromKey = (key) =>
  String(key || "").replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const placeOf = (item) => item?.locationDisplay || item?.country || "";

/** Hand-varied feature pick: prefer distinct places so the trio never mirrors the
 *  portfolio's top rows. Falls back to fill order if places repeat. */
function pickFeatured(items, n) {
  const pool = items.slice(1); // skip the hero (#0), already shown full-bleed
  const picked = [];
  const seen = new Set();
  for (const it of pool) {
    const key = it.country || it.locationDisplay || it.id;
    if (!seen.has(key)) {
      picked.push(it);
      seen.add(key);
    }
    if (picked.length >= n) return picked;
  }
  for (const it of pool) {
    if (picked.length >= n) break;
    if (!picked.includes(it)) picked.push(it);
  }
  return picked;
}

/** A wide frame to carry the "Around the world" band (not the hero). Prefers the
 *  pinned WORLD_IMAGE_ID, else the first wide non-hero frame. */
function pickWorldImage(items, heroId) {
  const pinned = items.find((it) => it.id === WORLD_IMAGE_ID && it.id !== heroId);
  if (pinned) return pinned;
  return (
    items.find((it) => it.gridRole === "panorama" && it.id !== heroId) ||
    items.find((it) => it.width && it.height && it.width / it.height >= 1.5 && it.id !== heroId) ||
    items[2] ||
    null
  );
}

export default function HomePage() {
  const reveal = useScrollReveal();
  const [items, setItems] = useState(null);
  const [heroUrl, setHeroUrl] = useState(null);
  const [heroBlurUrl, setHeroBlurUrl] = useState(null);
  const [collections, setCollections] = useState([]);
  const [locations, setLocations] = useState([]);
  const [worldReady, setWorldReady] = useState(false);
  const worldRef = useRef(null);

  useEffect(() => {
    let alive = true;
    // Both calls share the same underlying /public/portfolio fetch (see portfolioData.js),
    // so this is still exactly one network request.
    Promise.all([getPortfolioItems(), getHeroBlurDataUrl()])
      .then(([rows, blurUrl]) => {
        if (!alive) return;
        setItems(rows);
        setHeroBlurUrl(blurUrl);
        if (rows && rows[0]) {
          // Hero is a passive, above-the-fold background shown to every visitor —
          // use the web-optimized derivative, not the multi-MB original (that's
          // reserved for the deliberate lightbox zoom action in Portfolio.js).
          const url = thumbnailUrl(rows[0]);
          if (url) {
            const img = new Image();
            img.decoding = "async";
            img.onload = () => alive && setHeroUrl(url);
            img.src = url;
          }
        }
      })
      .catch((err) => console.error("Home hero fetch failed:", err));
    return () => {
      alive = false;
    };
  }, []);

  // Story bands are best-effort: a failed fetch just hides that section.
  useEffect(() => {
    let alive = true;
    getCollections()
      .then((rows) => {
        if (!alive) return;
        const byKey = new Map((rows || []).map((c) => [c.key, c]));
        const chosen = PREFERRED_COLLECTIONS.map((k) => byKey.get(k)).filter(
          (c) => c && Number(c.count) > 0
        );
        setCollections(chosen);
      })
      .catch(() => {});
    getLocations()
      .then((rows) => {
        if (!alive) return;
        setLocations(rows || []);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const heroItem = Array.isArray(items) ? items[0] : null;
  const featured = Array.isArray(items) ? pickFeatured(items, FEATURED_COUNT) : [];
  const worldItem = Array.isArray(items) ? pickWorldImage(items, heroItem?.id) : null;
  const worldUrl = worldItem ? thumbnailUrl(worldItem) : null;
  const countryCount = locations.length;

  // The world band sits well below the fold; don't let its full-res background
  // compete with the hero for bandwidth on first paint — only load it once the
  // section is about to scroll into view.
  useEffect(() => {
    setWorldReady(false);
    const el = worldRef.current;
    if (!el || !worldUrl) return undefined;
    if (typeof IntersectionObserver === "undefined") {
      setWorldReady(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setWorldReady(true);
          observer.disconnect();
        }
      },
      { rootMargin: "600px 0px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [worldUrl]);

  return (
    <div ref={reveal} className="home">
      <section className="home-hero">
        <div className="home-hero__gradient" />
        <div
          className={`home-hero__blur ${heroBlurUrl ? "is-ready" : ""}`}
          style={heroBlurUrl ? { backgroundImage: `url(${heroBlurUrl})` } : undefined}
        />
        <div
          className={`home-hero__bg ${heroUrl ? "is-ready" : ""}`}
          style={heroUrl ? { backgroundImage: `url(${heroUrl})` } : undefined}
        />
        <div className="home-hero__scrim" />
        <div className="home-hero__side" aria-hidden="true">
          <span>Landscape</span>
          <span>Nature</span>
          <span>Editions</span>
        </div>
        <div className="home-hero__inner">
          <p className="v-eyebrow">Fine-art landscape &amp; nature photography</p>
          <h1 className="v-display home-hero__title">
            Vast, silent, and <em>impossibly</em> close.
          </h1>
          {heroItem && (heroItem.aiTitle || heroItem.title) && (
            <p className="home-hero__caption">
              Above: <em>{heroItem.aiTitle || heroItem.title}</em>
              {placeOf(heroItem) ? ` · ${placeOf(heroItem)}` : ""}
            </p>
          )}
        </div>
        <div className="home-hero__cue" aria-hidden="true">
          Scroll ↓
        </div>
      </section>

      {featured.length > 0 && (
        <section className="v-section home-featured">
          <div className="v-section__head v-reveal">
            <div>
              <p className="v-eyebrow">Selected work</p>
              <h2 className="v-section__title v-display">A few that stayed with me</h2>
            </div>
            <Link className="v-section__link" to="/portfolio">
              View the full portfolio →
            </Link>
          </div>
          <div className="home-featured__row v-reveal">
            {featured.map((item) => (
              <Link key={item.id} to="/portfolio" className="home-feature">
                <div className="home-feature__media">
                  {thumbnailUrl(item) ? (
                    <img
                      src={thumbnailUrl(item)}
                      alt={item.aiAlt || item.aiTitle || item.title || "Photograph"}
                      loading="lazy"
                      decoding="async"
                      draggable={false}
                    />
                  ) : (
                    <div className="home-feature__placeholder" aria-hidden="true" />
                  )}
                </div>
                <div className="home-feature__caption">
                  <span className="home-feature__title">
                    {item.aiTitle || item.title || "Untitled"}
                  </span>
                  {placeOf(item) && (
                    <span className="home-feature__place">{placeOf(item)}</span>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {worldUrl && (
        <section
          ref={worldRef}
          className="home-world v-reveal"
          style={worldReady ? { backgroundImage: `url(${worldUrl})` } : undefined}
        >
          <div className="home-world__scrim" />
          <div className="home-world__inner">
            <p className="v-eyebrow home-world__eyebrow">Around the world</p>
            <h2 className="v-display home-world__title">
              From polar coasts to the equator.
            </h2>
            <p className="home-world__copy">
              These frames are gathered slowly and far afield — the glacial light of the
              north, the savannas of Tanzania, the dripping rainforests of Costa Rica.
              {countryCount ? ` Work from ${countryCount} countries and counting.` : ""}
            </p>
            {locations.length > 0 && (
              <p className="home-world__places">
                {locations.map((loc, i) => (
                  <React.Fragment key={loc.countryCode}>
                    {i > 0 && <span aria-hidden="true"> · </span>}
                    <Link to={`/locations/${loc.countryCode}`} className="home-world__place">
                      {loc.name}
                    </Link>
                  </React.Fragment>
                ))}
              </p>
            )}
            <Link className="home-world__cta" to="/locations">
              Explore by place →
            </Link>
          </div>
        </section>
      )}

      {collections.length > 0 && (
        <section className="v-section home-collections">
          <div className="v-section__head v-reveal">
            <div>
              <p className="v-eyebrow">Bodies of work</p>
              <h2 className="v-section__title v-display">Collections</h2>
            </div>
            <Link className="v-section__link" to="/collections">
              All collections →
            </Link>
          </div>
          <div className="home-collections__grid v-reveal">
            {collections.map((c) => {
              const title = c.name || c.title || titleFromKey(c.key);
              return (
                <Link
                  key={c.key}
                  to={`/collections/${c.key}`}
                  className="home-collection-card"
                  onMouseEnter={() => prefetchCollectionItems(c.key)}
                  onFocus={() => prefetchCollectionItems(c.key)}
                >
                  <div className="home-collection-card__media">
                    {c.thumbnailUrl ? (
                      <img src={c.thumbnailUrl} alt={title} loading="lazy" decoding="async" draggable={false} />
                    ) : (
                      <div className="home-collection-card__placeholder" aria-hidden="true" />
                    )}
                  </div>
                  <div className="home-collection-card__label">
                    <span className="home-collection-card__title">{title}</span>
                    {Number(c.count) > 0 && (
                      <span className="home-collection-card__count">
                        {c.count} photograph{Number(c.count) === 1 ? "" : "s"}
                      </span>
                    )}
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}

    </div>
  );
}
